import mapboxgl from 'mapbox-gl';
import { EventBus } from './EventBus';

export function setHighlightColor(map) {
    map.U.setCircleStrokeColor(
        window.app.stages.map((stage) => `schools-highlighted-${stage}`),
        `hsla(${
            window.app.hues[window.app.BoundarySelector.year]
        }, 65%, 50%, 0.7)`
    );
}

let labelPopup;
function hoverLabel(map, layer, labelFunc) {
    labelPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'labelPopup',
    });
    window.map.on('mouseenter', layer, (e) => {
        const f = e.features[0],
            p = f.properties;

        labelPopup
            .setLngLat(f.geometry.coordinates)
            .setHTML(labelFunc(p))
            .addTo(map);
    });
    window.map.on('mouseleave', layer, (e) => labelPopup.remove());
}

function activeSchoolFilter(year, stage) {
    return ['==', ['get', `${year}_${stage}`], 'x'];
}
function inactiveSchoolFilter(year, stage) {
    return ['!', activeSchoolFilter(year, stage)];
}

export function addSchoolLayers(map, tileHost) {
    // map.U.loadImage('school', 'static/school.png');
    map.U.loadImage('school-red', 'static/school.png');
    map.U.loadImage('school-blue', 'static/school-blue.png');
    map.U.loadImage('school-green', 'static/school-green.png');
    map.U.loadImage('school-purple', 'static/school-purple.png');
    map.U.loadImage('school-min', 'static/school-min.png');
    map.U.loadImage('school-grey', 'static/school-grey.png');
    map.U.loadImage('geocode-pin', '/darkblue-map-marker.png');

    for (const stage of window.app.stages) {
        // console.log(window.app.schools[stage]);
        window.map.U.addGeoJSON(`schools-${stage}`, window.app.schools[stage]);

        // non-selectable schools
        window.map.U.addSymbol(`etc-schools-${stage}`, `schools-${stage}`, {
            textFont: !window.app.showBasemap
                ? ['Klokantech Noto Sans Regular']
                : ['DIN Offc Pro Italic'],
            textSize: 10,
            textOffset: [1.5, 0],
            textJustify: 'left',
            // textHaloColor: 'white',
            textHaloColor: 'hsl(80, 11.1%, 94.7%)',
            textHaloWidth: 3,
            textOptional: true,
            textAllowOverlap: ['step', ['zoom'], false, 14, true],

            iconImage: 'school-min',
            iconSize: {
                stops: [
                    [14, 0.3],
                    [15, 0.4],
                ],
            },
            minzoom: 12,
            filter: ['!in', 'Campus_Type', 'Pri/Sec', 'Primary', 'Secondary'],
            visibility: 'none',
        });

        hoverLabel(
            map,
            `etc-schools-${stage}`,
            (p) => '<p class="non-selectable-school">' + p.School_Name
        );

        for (const year of window.app.schoolsetYears) {
            const id = `schools-${year}-${stage}`;
            window.map.U.addSymbol(
                `${id}-inactive-labels`,
                `schools-${stage}`,
                {
                    textField: {
                        stops: [
                            [13, ''],
                            [14, '{School_Name}'],
                        ],
                    },
                    textAnchor: 'left',
                    textFont: !window.app.showBasemap
                        ? ['Klokantech Noto Sans Regular']
                        : ['DIN Offc Pro Italic'],
                    textSize: 10,
                    textOffset: [1.5, 0],
                    textJustify: 'left',
                    // textHaloColor: 'white',
                    textHaloColor: 'hsl(80, 11.1%, 94.7%)',
                    textHaloWidth: 5,
                    textOptional: true,
                    iconImage: [
                        'match',
                        ['get', 'Campus_Type'],
                        'Primary',
                        'school-grey',
                        'Secondary',
                        'school-grey',
                        'Pri/Sec',
                        'school-grey',
                        'school-min',
                    ],
                    iconSize: {
                        stops: [
                            [12, 0.2],
                            [15, 0.3],
                        ],
                    },
                    iconOffset: [0, -5], // lines up better in the circle
                    iconAllowOverlap: false,
                    textAllowOverlap: false,
                    textIgnorePlacement: true,
                    iconIgnorePlacement: true,
                    minzoom: 12,

                    visibility: 'none',
                    filter: inactiveSchoolFilter(year, stage),
                }
            );
            hoverLabel(
                window.map,
                `${id}-inactive-labels`,
                (p) => p.School_Name
            );

            window.map.U.addSymbol(`${id}-active-labels`, `schools-${stage}`, {
                textField: {
                    stops: [
                        [12, ''],
                        [13, '{School_Name}'],
                    ],
                },
                textAnchor: 'left',
                textFont: !window.app.showBasemap
                    ? ['Klokantech Noto Sans Regular']
                    : ['DIN Offc Pro Regular'],
                textSize: 12,
                textOffset: [1.5, 0],
                textJustify: 'left',
                // textHaloColor: 'white',
                textHaloColor: 'hsl(80, 11.1%, 94.7%)', // map background
                textHaloWidth: 5,
                textOptional: true,
                iconImage:
                    {
                        primary: 'school-green',
                        specialist: 'school-red',
                        other: 'school-purple',
                    }[year] || 'school-blue',
                iconSize: {
                    stops: [
                        [13, 0.4],
                        [14, 0.5],
                    ],
                },
                iconAllowOverlap: ['step', ['zoom'], false, 13, true],
                iconOptional: false,
                visibility: 'none',
                filter: activeSchoolFilter(year, stage),
            });
            window.map.U.hoverPointer(`${id}-active-labels`);
            window.map.U.hoverPointer(`${id}-inactive-labels`);
            hoverLabel(window.map, `${id}-active-labels`, (p) => p.School_Name);

            window.map.U.clickLayer(`${id}-active-labels`, (e) => {
                const school = e.features[0];
                EventBus.$emit('school-select', school);
                window.track(
                    'Map',
                    'click-school',
                    school.properties.Entity_Code
                );
            });
            window.map.U.clickLayer(`${id}-inactive-labels`, (e) => {
                const school = e.features[0];
                EventBus.$emit('school-select', school);

                // as we switch boundaries, the old set of nearest schools is now irrelevant
                window.app.NearestSchools.schools = null;
                // window.app.OtherZonedSchools.schoolsBoundaries = null;
                EventBus.$emit('boundaries-results', { zonedSchools: [] });
                window.track(
                    'Map',
                    'click-inactive-school',
                    school.properties.Entity_Code
                );
                window.app.BoundarySelector.findBoundarySetForSchool(school);
            });
        }
        window.map.U.addCircle(
            `schools-highlighted-${stage}`,
            `schools-${stage}`,
            {
                circleColor: 'transparent',
                circleRadius: 30,

                circleStrokeWidth: 4,
                // circleStrokeOpacity:0.5,
                filter: false, // controlled through filter rather than visibility property
            }
        );
    }
    setHighlightColor(map);
}

export function switchSchoolSet(map, newSchoolSet, oldSchoolSet) {
    if (oldSchoolSet) {
        window.setTimeout(
            () =>
                map.U.hide([
                    `schools-${oldSchoolSet}-active-labels`,
                    `schools-${oldSchoolSet}-inactive-labels`,
                ]),
            150
        );
    }
    if (newSchoolSet) {
        map.U.show([
            `schools-${newSchoolSet}-active-labels`,
            `schools-${newSchoolSet}-inactive-labels`,
        ]);
    }
    for (const stage of window.app.stages) {
        map.U.toggle(
            `etc-schools-${stage}`,
            window.app.BoundarySelector.stage === stage
        );
    }
}

export function updateSpecialisationFilter(map, filterId) {
    const stage = window.app.BoundarySelector.stage;
    const year = 'specialist';
    const schoolSet = `${year}-${stage}`;
    const specFilter = window.app.BoundarySelector
        .selectedSpecialisationCategories
        ? [
              [
                  'any',
                  ...window.app.BoundarySelector.selectedSpecialisationCategories.map(
                      (cat) => ['==', ['get', 'Specialisation'], cat]
                  ),
              ],
          ]
        : [];
    const filter = [
        'all',
        activeSchoolFilter('specialist', stage),
        ...specFilter,
    ];
    map.U.setFilter(`schools-${schoolSet}-active-labels`, filter);
}

export function fitPoints(map, ...points) {
    map.fitBounds(getBbox(...points), {
        padding:
            Math.min(
                map.getContainer().clientWidth,
                map.getContainer().clientHeight
            ) * 0.3,
    });
}

function getBbox(...points) {
    points = points.filter((p) => p); // ignore undefineds
    const point = points[0];
    let bbox = [point[0], point[1], point[0], point[1]];
    for (const point of points) {
        bbox = [
            Math.min(bbox[0], point[0]),
            Math.min(bbox[1], point[1]),
            Math.max(bbox[2], point[0]),
            Math.max(bbox[3], point[1]),
        ];
    }
    return bbox;
}

export function highlightSchools(map, { filter, schools, zoomTo, schoolSet }) {
    function zoomToSchools(schools, zoomTo) {
        const coordses = schools.map((school) => school.geometry.coordinates);
        if (window.app.AddressSearch.addressPoint) {
            fitPoints(map, ...coordses, window.app.AddressSearch.addressPoint);
        } else {
            const method = zoomTo === 'jump' ? 'jumpTo' : 'flyTo';
            map[method]({
                center: schools[0].geometry.coordinates,
                zoom: Math.max(map.getZoom(), 14),
            });
        }
    }

    if (zoomTo && schools && schools.length) {
        zoomToSchools(schools, zoomTo);
    }
    map.U.setFilter(/schools-highlighted/, filter);

    const [year, stage] = window.app.BoundarySelector.year_stage.split(/_/);
    // temporarily (!) expand the active layer to include the extra school
    map.U.setFilter(`schools-${year}-${stage}-active-labels`, [
        'any',
        activeSchoolFilter(year, stage),
        filter,
    ]);

    if (schoolSet && schoolSet.match(/other/)) {
        map.U.setFilter(`schools-${schoolSet}-active-labels`, filter);
    }
    setHighlightColor(map);
}
